import Tabs from "./tabs";
import { useState } from "react";
import { Grid, Box, Stack, Typography, Avatar, Button } from "@mui/material";
import SmartToyIcon from '@mui/icons-material/SmartToy';
import KeyIcon from '@mui/icons-material/Key';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import SnackbarMessage from "../../../components/snackBar/snackBar";
import { useNavigate } from "react-router-dom";
import { setChatBotDetails } from "../chatBotSlice";
import { APP_ROUTES } from "../../../config/appRoutes";
import { useDispatch } from "react-redux";
import { FieldTextField } from "../../../components/selections/fieldInputs";
import { stepCardSx, stepActionsSx, backBtnSx, nextBtnSx, StepHeader } from "../../campaign/components/stepUi";
import convoBg from '../../../assests/convoBg.jpg';

const BLUE = "#2563eb";

// "How it works" steps (display only)
const HOW_STEPS = [
    { title: "Name your bot", text: "A name only your team sees, e.g. Admissions Bot." },
    { title: "Pick a keyword", text: "When a customer sends this word on WhatsApp, the bot starts." },
    { title: "Build the flow", text: "Next you'll add the bot's replies and options." },
];

// Create form. `embedded` = compact version for the popup (fields + Cancel / Create Bot).
export function ChatBotCreateForm({ embedded = false, onCancel }) {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [state, setState] = useState({
        chatBotName: "",
        chatBotKey: "",
        errors: {},
    });

    const { chatBotName, chatBotKey } = state;

    const handleChange = (name, value) => {
        let newValue = value;
        if (name === "chatBotName") {
            newValue = value.replace(/^\s+/, "").slice(0, 20);
        } else if (name === "chatBotKey") {
            newValue = value.replace(/\s/g, "").slice(0, 20);
        }

        setState((prev) => ({
            ...prev,
            [name]: newValue,
            errors: { ...prev.errors, [name]: "" },
        }));
    };


    const chatBotFields = [
        { id: 1, name: "chatBotName", value: chatBotName, label: "ChatBot Name", placeholder: "e.g. Admissions Bot", icon: <SmartToyIcon /> },
        { id: 2, name: "chatBotKey", value: chatBotKey, label: "ChatBot Key", placeholder: "e.g. admission (no spaces)", icon: <KeyIcon /> },
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validations = [
            { valid: !!chatBotName, message: "Bot Name is required", field: "chatBotName" },
            { valid: !!chatBotKey, message: "Bot Key is required", field: "chatBotKey" },
        ];

        for (let rule of validations) {
            if (!rule.valid) {
                setSnackbar((prev) => ({
                    ...prev,
                    open: true,
                    message: rule.message,
                    severity: "error",
                }));

                setState((prev) => ({
                    ...prev,
                    errors: { ...prev.errors, [rule.field]: rule.message },
                }));
                return;
            }
        }
        navigate(APP_ROUTES.CHATBOT.CHATBOT_FLOW);
        dispatch(setChatBotDetails([{
            chatBotName: chatBotName,
            chatBotKey: chatBotKey
        }]))
    };

    const renderFields = () => (
        // both fields on one line (stacked on phones)
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            {chatBotFields.map((field) => (
                <Box key={field.id} sx={{ flex: 1, minWidth: 0 }}>
                    <FieldTextField
                        name={field.name}
                        label={field.label}
                        value={state[field.name]}
                        onChange={(e) => handleChange(field.name, e.target.value)}
                        error={!!state.errors[field.name]}
                        maxLength={20}
                        required
                        icon={field.icon}
                        placeholder={field.placeholder}
                    />
                    <Typography sx={{ mt: 0.5, textAlign: "right", fontSize: 12, fontWeight: 600, color: "#94a3b8" }}>
                        {state[field.name].length}/20
                    </Typography>
                </Box>
            ))}
        </Stack>
    );

    const snackbarEl = (
        <SnackbarMessage open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} />
    );

    if (embedded) {
        return (
            <Box>
                <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                    {renderFields()}
                    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1, p: 1.25, borderRadius: 2.5, bgcolor: "#f8fafc", border: "1px dashed #cbd5e1" }}>
                        <KeyIcon sx={{ fontSize: 17, color: BLUE, mt: 0.15 }} />
                        <Typography sx={{ fontSize: 12.5, color: "#64748b", lineHeight: 1.5 }}>
                            When a customer sends the keyword on WhatsApp, the bot starts. Next you will build the bot flow.
                        </Typography>
                    </Stack>
                </Box>
                <Box sx={{ ...stepActionsSx, px: { xs: 2, sm: 2.5 } }}>
                    <Stack direction="row" spacing={1.5}>
                        <Button onClick={onCancel} sx={backBtnSx}>Cancel</Button>
                        <Button onClick={handleSubmit} endIcon={<ArrowForwardIcon />} sx={nextBtnSx}>Create Bot</Button>
                    </Stack>
                </Box>
                {snackbarEl}
            </Box>
        );
    }

    return (
        <div>
            <Tabs>
                <Grid container spacing={2.5} sx={{ mt: 1 }}>
                    {/* ---------- Form ---------- */}
                    <Grid size={{ xs: 12, md: 7 }}>
                        <Box sx={{ ...stepCardSx, bgcolor: "#fff", height: "100%", display: "flex", flexDirection: "column" }}>
                            <Box sx={{ p: { xs: 2, sm: 3 }, flex: 1 }}>
                                <StepHeader
                                    icon={<SmartToyIcon sx={{ fontSize: 20 }} />}
                                    title="Bot Details"
                                    subtitle="Give your bot a name and the keyword that starts it"
                                />
                                {renderFields()}
                            </Box>

                            <Box sx={stepActionsSx}>
                                <Stack direction="row" spacing={1.5}>
                                    <Button onClick={handleSubmit} endIcon={<ArrowForwardIcon />} sx={nextBtnSx}>
                                        Create Bot
                                    </Button>
                                </Stack>
                            </Box>
                        </Box>
                    </Grid>

                    {/* ---------- How it works + keyword preview ---------- */}
                    <Grid size={{ xs: 12, md: 5 }}>
                        <Box sx={{ ...stepCardSx, bgcolor: "#fff", p: { xs: 2, sm: 2.5 }, height: "100%" }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#0f172a", mb: 1.5 }}>How it works</Typography>
                            <Stack spacing={1.5}>
                                {HOW_STEPS.map((s, i) => (
                                    <Stack key={s.title} direction="row" spacing={1.25} alignItems="flex-start">
                                        <Box sx={{ width: 24, height: 24, flexShrink: 0, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: "#fff", bgcolor: BLUE }}>
                                            {i + 1}
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a", lineHeight: 1.4 }}>{s.title}</Typography>
                                            <Typography sx={{ fontSize: 12.5, color: "#64748b", lineHeight: 1.45 }}>{s.text}</Typography>
                                        </Box>
                                    </Stack>
                                ))}
                            </Stack>

                            {/* mini WhatsApp preview of the keyword */}
                            <Box sx={{ mt: 2.25, borderRadius: 2.5, overflow: "hidden", border: "1px solid #e9edf2" }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.25, py: 0.75, bgcolor: "#075e54", color: "#fff" }}>
                                    <Avatar sx={{ width: 26, height: 26, bgcolor: "#25d366" }}>
                                        <SmartToyIcon sx={{ fontSize: 15 }} />
                                    </Avatar>
                                    <Typography noWrap sx={{ fontSize: 13, fontWeight: 700 }}>{chatBotName || "Your bot"}</Typography>
                                </Stack>
                                <Box sx={{ p: 1.5, bgcolor: "#efeae2", backgroundImage: `url(${convoBg})`, backgroundRepeat: "repeat", backgroundSize: "340px auto" }}>
                                    <Stack alignItems="flex-end">
                                        <Box sx={{ px: 1.25, py: 0.6, maxWidth: "80%", borderRadius: 2, borderTopRightRadius: 0, bgcolor: "#d9fdd3", boxShadow: "0 1px 1px rgba(11,20,26,0.13)" }}>
                                            <Typography sx={{ fontSize: 13.5, color: "#111b21", wordBreak: "break-word" }}>
                                                {chatBotKey || "keyword"}
                                            </Typography>
                                            <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.3}>
                                                <Typography sx={{ fontSize: 10.5, color: "#667781" }}>Customer</Typography>
                                                <DoneAllIcon sx={{ fontSize: 13, color: "#53bdeb" }} />
                                            </Stack>
                                        </Box>
                                    </Stack>
                                    <Box sx={{ mt: 1, px: 1.25, py: 0.6, width: "fit-content", maxWidth: "80%", borderRadius: 2, borderTopLeftRadius: 0, bgcolor: "#fff", boxShadow: "0 1px 1px rgba(11,20,26,0.13)" }}>
                                        <Typography sx={{ fontSize: 13, color: "#667781", fontStyle: "italic" }}>Bot replies with your flow…</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Tabs>
            {snackbarEl}
        </div>
    );
}

// /chatbot-creation page
export default function ChatBotCreation() {
    return <ChatBotCreateForm />;
}
